/**
 * Fetch utility with timeout support
 * Prevents requests from hanging indefinitely on slow networks
 */

export class FetchTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Request timeout after ${timeoutMs}ms`);
    this.name = "FetchTimeoutError";
  }
}

/**
 * Fetch with automatic timeout
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @param timeoutMs - Timeout in milliseconds (default: 30000ms / 30s)
 * @returns Promise<Response>
 * @throws FetchTimeoutError if request exceeds timeout
 */
export async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs = 30000
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    // Check if error was due to abort (timeout)
    if (error instanceof Error && error.name === "AbortError") {
      throw new FetchTimeoutError(timeoutMs);
    }

    // Re-throw other errors
    throw error;
  }
}

/**
 * Fetch with timeout and automatic retry on transient failures
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @param config - Configuration for timeout and retries
 * @returns Promise<Response>
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  config: {
    timeoutMs?: number;
    maxRetries?: number;
    retryOn5xx?: boolean;
  } = {}
): Promise<Response> {
  const { timeoutMs = 30000, maxRetries = 3, retryOn5xx = true } = config;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, timeoutMs);

      // Retry on 5xx errors (server errors) if configured
      if (retryOn5xx && response.status >= 500 && attempt < maxRetries - 1) {
        const backoffMs = Math.min(1000 * 2 ** attempt, 10000);
        await new Promise((resolve) => setTimeout(resolve, backoffMs));
        continue;
      }

      return response;
    } catch (error) {
      // Don't retry on last attempt
      if (attempt === maxRetries - 1) {
        throw error;
      }

      // Exponential backoff: 1s, 2s, 4s (max 10s)
      const backoffMs = Math.min(1000 * 2 ** attempt, 10000);
      await new Promise((resolve) => setTimeout(resolve, backoffMs));
    }
  }

  throw new Error("Max retries exceeded");
}
